/**
 * Parent dashboard — profile, linked student feedback, assigned tasks, history
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import RoleDashboardShell from '../components/RoleDashboardShell';
import {
  parentMenuItems,
  parentMenuGroups,
  isValidParentMenuId,
} from '../config/roleDashboardMenus';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'pending', label: 'Pending' },
  { id: 'completed', label: 'Completed' },
  { id: 'upcoming', label: 'Upcoming' },
];

const TASK_LABELS = {
  ADMISSION: 'Admission',
  EVENT: 'Event',
  DEPARTMENT: 'Department / organization',
  PROGRAM: 'Overall program',
  SEMINAR: 'Seminar',
};

function openParentWindow(path) {
  const base = process.env.PUBLIC_URL || '';
  const url = `${window.location.origin}${base}${path}`;
  const popup = window.open(url, '_blank');
  if (popup) {
    popup.opener = null;
    return;
  }
  window.location.assign(url);
}

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

function initials(name) {
  return (name || 'PA')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function Field({ label, value }) {
  return (
    <div className="profile-field">
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );
}

function TaskCard({ task, onSubmit, onPreview }) {
  return (
    <article className={`stu-subject-card is-${task.status}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
            {TASK_LABELS[task.taskType] || task.taskType}
          </p>
          <h3 className="text-lg font-semibold m-0" style={{ color: 'var(--text-primary)' }}>{task.title}</h3>
        </div>
        <span className={`stu-chip stu-chip-${task.status}`}>{task.status}</span>
      </div>
      <p className="text-sm m-0" style={{ color: 'var(--text-secondary)' }}>
        {[task.cycleName, task.academicYear, task.eventName].filter(Boolean).join(' · ')}
      </p>
      {task.description ? <p className="text-sm m-0" style={{ color: 'var(--text-muted)' }}>{task.description}</p> : null}
      <p className="text-xs m-0" style={{ color: 'var(--text-muted)' }}>
        {formatDate(task.startAt)} – {formatDate(task.endAt)}
        {task.status === 'pending' && !task.submissionId ? ' · Action needed before the window closes' : ''}
        {task.status === 'completed' ? ' · Locked · cannot edit' : ''}
      </p>
      <div className="flex flex-wrap gap-2 mt-auto">
        {(task.status === 'active' || task.status === 'pending') && (
          <button type="button" className="btn-primary" disabled={!task.canSubmit} onClick={() => onSubmit(task)}>
            Submit feedback
          </button>
        )}
        {task.status === 'completed' && (
          <button type="button" className="btn-secondary" onClick={() => onPreview(task)}>Preview</button>
        )}
        {task.status === 'upcoming' && (
          <button type="button" className="btn-secondary" disabled>Opens with the next window</button>
        )}
      </div>
    </article>
  );
}

export default function ParentDashboard() {
  const { user } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [childStatusFilter, setChildStatusFilter] = useState('all');
  const [academicYear, setAcademicYear] = useState('');
  const [profileForm, setProfileForm] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');

  const fetchWorkspace = useCallback(async (year) => {
    try {
      const params = year ? { academicYear: year } : {};
      const res = await api.get('/parent-feedback/dashboard', { params });
      const data = res.data?.data || null;
      setWorkspace(data);
      if (data?.studentFeedback?.selectedYear && !year) {
        setAcademicYear(data.studentFeedback.selectedYear);
      }
      if (data?.profile) {
        setProfileForm({
          contactNumber: data.profile.contactNumber || '',
          address: data.profile.address || '',
          city: data.profile.city || '',
          qualification: data.profile.qualification || '',
          highestQualification: data.profile.highestQualification || '',
          designation: data.profile.designation || '',
          yearsOfExperience: data.profile.yearsOfExperience || '',
          workingDomain: data.profile.workingDomain || '',
          expertiseAreas: data.profile.expertiseAreas || '',
          companyName: data.profile.companyName || '',
          companyLocation: data.profile.companyLocation || '',
          industry: data.profile.industry || '',
          linkedinUrl: data.profile.linkedinUrl || '',
          profileSummary: data.profile.profileSummary || '',
        });
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load parent dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWorkspace(academicYear);
    const onFocus = () => fetchWorkspace(academicYear);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [fetchWorkspace, academicYear]);

  const profile = workspace?.profile;
  const student = workspace?.student;
  const term = workspace?.term;
  const stats = workspace?.stats || { active: 0, pending: 0, completed: 0, upcoming: 0 };
  const childStats = workspace?.studentFeedback?.stats || { active: 0, pending: 0, completed: 0, upcoming: 0 };
  const displayName = profile?.firstName
    ? `${profile.firstName} ${profile.lastName || ''}`.trim()
    : (user?.parent ? `${user.parent.first_name} ${user.parent.last_name}` : user?.email);
  const childName = student ? `${student.firstName} ${student.lastName || ''}`.trim() : null;
  const termLabel = term?.term_type === 'EVEN' ? 'Even' : term?.term_type === 'ODD' ? 'Odd' : null;

  const filteredTasks = useMemo(() => {
    const list = workspace?.tasks || [];
    return statusFilter === 'all' ? list : list.filter((task) => task.status === statusFilter);
  }, [workspace, statusFilter]);

  const filteredChildSubjects = useMemo(() => {
    const list = workspace?.studentFeedback?.subjects || [];
    return childStatusFilter === 'all' ? list : list.filter((item) => item.status === childStatusFilter);
  }, [workspace, childStatusFilter]);

  const handleSubmit = (task) => {
    if (!task.canSubmit) return;
    openParentWindow(`/dashboard/parent/feedback?taskId=${task.id}`);
  };

  const handlePreview = (task) => {
    if (!task.submissionId) return;
    openParentWindow(`/dashboard/parent/preview/${task.submissionId}`);
  };

  const handleChildPreview = (subject) => {
    if (!subject.submissionId) return;
    openParentWindow(`/dashboard/parent/student-preview/${subject.submissionId}`);
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileSaving(true);
    setProfileMessage('');
    setError('');
    try {
      const res = await api.patch('/parent-feedback/profile', profileForm);
      setWorkspace(res.data?.data || workspace);
      setProfileMessage('Your parent profile was saved.');
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Could not update profile');
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <RoleDashboardShell
      title="Parent Dashboard"
      subtitle={`${displayName}${childName ? ` · Parent of ${childName}` : ''}${profile?.companyName ? ` · ${profile.companyName}` : ''}`}
      brandTitle="Parent Desk"
      brandSubtitle={term ? `${term.term_type} term · ${term.academic_year}` : 'Parent console'}
      menuItems={parentMenuItems}
      menuGroups={parentMenuGroups}
      isValidMenuId={isValidParentMenuId}
      loading={loading}
      error={error}
    >
      {({ activeMenu }) => {
        if (activeMenu === 'overview') {
          return (
            <div className="space-y-6">
              <div className="par-hero">
                <div className="stu-hero-row">
                  <div className="stu-hero-main">
                    <span className="stu-avatar">{initials(displayName)}</span>
                    <div>
                      <p className="text-sm text-white/80 mb-1">Welcome back</p>
                      <h2 className="text-2xl font-semibold m-0">{displayName}</h2>
                      <p className="mt-1 text-white/85 text-sm">
                        {[profile?.parentCode, profile?.relation || 'Parent', childName, student?.rollNumber]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </div>
                  </div>
                  <div className="stu-hero-cycle">
                    <p className="text-xs uppercase tracking-wide text-white/70 m-0">Active semester window</p>
                    <p className="font-semibold m-0 mt-1">
                      {termLabel ? `${termLabel} semester` : 'Term not published'}
                      {term?.academic_year ? ` · ${term.academic_year}` : ''}
                    </p>
                    <p className="text-sm text-white/80 m-0 mt-1">Only events assigned by admin appear here.</p>
                  </div>
                </div>
              </div>

              <div className="stu-stat-grid">
                {['active', 'pending', 'completed', 'upcoming'].map((key) => (
                  <button key={key} type="button" className={`stu-stat stu-stat-${key} text-left`} onClick={() => setStatusFilter(key)}>
                    <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{key}</p>
                    <p className="stu-stat-value">{stats[key] || 0}</p>
                  </button>
                ))}
              </div>

              <div className="stu-filter-bar">
                {STATUS_FILTERS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    onClick={() => setStatusFilter(filter.id)}
                    className={`stu-filter ${statusFilter === filter.id ? 'is-on' : ''}`}
                  >
                    {filter.label}
                    {filter.id !== 'all' ? ` · ${stats[filter.id] || 0}` : ` · ${workspace?.tasks?.length || 0}`}
                  </button>
                ))}
              </div>

              {filteredTasks.length === 0 ? (
                <div className="admin-empty">
                  <p className="admin-empty-title">No assigned feedback</p>
                  <p className="admin-empty-text">When admin assigns admission, event, department, program, or seminar feedback, it will appear here.</p>
                </div>
              ) : (
                <div className="stu-subject-grid">
                  {filteredTasks.map((task) => (
                    <TaskCard key={task.id} task={task} onSubmit={handleSubmit} onPreview={handlePreview} />
                  ))}
                </div>
              )}
            </div>
          );
        }

        if (activeMenu === 'student') {
          const years = workspace?.studentFeedback?.academicYears || [];
          return (
            <div className="space-y-6">
              <div className="par-hero">
                <p className="text-sm text-white/80 m-0">Linked student</p>
                <h2 className="text-2xl font-semibold m-0 mt-1">{childName || 'No student linked'}</h2>
                <p className="mt-2 text-white/85 text-sm m-0">
                  {[student?.rollNumber, student?.department, student?.programTitle, student?.semesterTitle, termLabel ? `${termLabel} term` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              {student ? (
                <div className="admin-card">
                  <div className="admin-card-body">
                    <h2 className="admin-section-title">Student details</h2>
                    <dl className="profile-grid">
                      <Field label="Full name" value={childName} />
                      <Field label="Roll / PRN" value={student.prn || student.rollNumber} />
                      <Field label="Department" value={student.department} />
                      <Field label="Program" value={student.programTitle} />
                      <Field label="Year" value={student.yearOfStudy} />
                      <Field label="Semester" value={student.semesterTitle} />
                      <Field label="Batch" value={student.batch} />
                      <Field label="Status" value={student.studentStatus} />
                    </dl>
                  </div>
                </div>
              ) : (
                <div className="admin-empty">
                  <p className="admin-empty-title">Student not linked</p>
                  <p className="admin-empty-text">Ask admin to link your son or daughter to this parent login.</p>
                </div>
              )}

              <div className="admin-card">
                <div className="admin-card-body space-y-4">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <h2 className="admin-section-title mb-1">Student feedback</h2>
                      <p className="admin-section-subtitle mb-0">Read-only. Filter by academic year to see active and completed semester feedback.</p>
                    </div>
                    <div>
                      <label className="input-label">Academic year</label>
                      <select className="input min-w-[160px]" value={academicYear} onChange={(e) => setAcademicYear(e.target.value)}>
                        {years.length === 0 ? <option value="">No years yet</option> : null}
                        {years.map((year) => (
                          <option key={year} value={year}>{year}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="stu-stat-grid">
                    {['active', 'pending', 'completed', 'upcoming'].map((key) => (
                      <button key={key} type="button" className={`stu-stat stu-stat-${key} text-left`} onClick={() => setChildStatusFilter(key)}>
                        <p className="text-xs uppercase tracking-wide" style={{ color: 'var(--text-muted)' }}>{key}</p>
                        <p className="stu-stat-value">{childStats[key] || 0}</p>
                      </button>
                    ))}
                  </div>
                  <div className="stu-filter-bar">
                    {STATUS_FILTERS.map((filter) => (
                      <button
                        key={filter.id}
                        type="button"
                        onClick={() => setChildStatusFilter(filter.id)}
                        className={`stu-filter ${childStatusFilter === filter.id ? 'is-on' : ''}`}
                      >
                        {filter.label}
                        {filter.id !== 'all' ? ` · ${childStats[filter.id] || 0}` : ` · ${workspace?.studentFeedback?.subjects?.length || 0}`}
                      </button>
                    ))}
                  </div>
                  {filteredChildSubjects.length === 0 ? (
                    <div className="admin-empty">
                      <p className="admin-empty-title">No subject feedback for this year</p>
                      <p className="admin-empty-text">When your ward submits semester feedback, preview will appear here.</p>
                    </div>
                  ) : (
                    <div className="stu-subject-grid">
                      {filteredChildSubjects.map((subject) => (
                        <article key={`${subject.id}-${subject.submissionId || 'open'}`} className={`stu-subject-card is-${subject.status}`}>
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{subject.code}</p>
                              <h3 className="text-lg font-semibold m-0" style={{ color: 'var(--text-primary)' }}>{subject.name}</h3>
                            </div>
                            <span className={`stu-chip stu-chip-${subject.status}`}>{subject.status}</span>
                          </div>
                          <p className="text-sm m-0" style={{ color: 'var(--text-secondary)' }}>
                            {[subject.facultyName, subject.cycleName, subject.academicYear].filter(Boolean).join(' · ')}
                          </p>
                          {subject.status === 'pending' ? (
                            <p className="text-xs m-0" style={{ color: 'var(--text-muted)' }}>Pending — your ward has not submitted this subject yet.</p>
                          ) : null}
                          {subject.status === 'completed' ? (
                            <p className="text-xs m-0" style={{ color: 'var(--text-muted)' }}>Overall {subject.overallRating ?? '—'} · {formatDate(subject.submittedAt)}</p>
                          ) : null}
                          <div className="flex flex-wrap gap-2 mt-auto">
                            {subject.canPreview ? (
                              <button type="button" className="btn-secondary" onClick={() => handleChildPreview(subject)}>Preview</button>
                            ) : (
                              <button type="button" className="btn-secondary" disabled>
                                {subject.status === 'pending' ? 'Waiting on student' : 'Not open'}
                              </button>
                            )}
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        if (activeMenu === 'profile') {
          return (
            <div className="admin-card">
              <div className="admin-card-body">
                <div className="stu-identity">
                  <span className="stu-avatar-sm" style={{ width: '3.25rem', height: '3.25rem', fontSize: '1.1rem' }}>{initials(displayName)}</span>
                  <div>
                    <h2 className="admin-section-title mb-1">Parent profile</h2>
                    <p className="admin-section-subtitle mb-0">Identity from the master record is read-only. You may update contact, qualification, work, and company details.</p>
                  </div>
                </div>
                {!profile ? (
                  <div className="admin-empty">
                    <p className="admin-empty-title">Profile not linked</p>
                    <p className="admin-empty-text">Ask admin to attach a parent master record to this login.</p>
                  </div>
                ) : (
                  <>
                    <dl className="profile-grid mb-8">
                      <Field label="Full name" value={displayName} />
                      <Field label="Parent code" value={profile.parentCode} />
                      <Field label="Email" value={profile.email || user?.email} />
                      <Field label="Relation" value={profile.relation} />
                      <Field label="Linked student" value={childName} />
                      <Field label="Designation" value={profile.designation} />
                      <Field label="Company" value={profile.companyName} />
                      <Field label="Working domain" value={profile.workingDomain} />
                    </dl>
                    {profileForm ? (
                      <form onSubmit={handleSaveProfile} className="space-y-4">
                        <h3 className="admin-section-title">Editable details</h3>
                        {profileMessage ? <p className="text-sm text-emerald-700">{profileMessage}</p> : null}
                        <div className="grid gap-4 sm:grid-cols-2">
                          <div>
                            <label className="input-label">Contact number</label>
                            <input className="input" value={profileForm.contactNumber} onChange={(e) => setProfileForm((f) => ({ ...f, contactNumber: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">City</label>
                            <input className="input" value={profileForm.city} onChange={(e) => setProfileForm((f) => ({ ...f, city: e.target.value }))} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="input-label">Address</label>
                            <input className="input" value={profileForm.address} onChange={(e) => setProfileForm((f) => ({ ...f, address: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Qualification</label>
                            <input className="input" value={profileForm.qualification} onChange={(e) => setProfileForm((f) => ({ ...f, qualification: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Highest qualification</label>
                            <input className="input" value={profileForm.highestQualification} onChange={(e) => setProfileForm((f) => ({ ...f, highestQualification: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Designation</label>
                            <input className="input" value={profileForm.designation} onChange={(e) => setProfileForm((f) => ({ ...f, designation: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Years of experience</label>
                            <input className="input" value={profileForm.yearsOfExperience} onChange={(e) => setProfileForm((f) => ({ ...f, yearsOfExperience: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Working domain</label>
                            <input className="input" value={profileForm.workingDomain} onChange={(e) => setProfileForm((f) => ({ ...f, workingDomain: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Industry</label>
                            <input className="input" value={profileForm.industry} onChange={(e) => setProfileForm((f) => ({ ...f, industry: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Company</label>
                            <input className="input" value={profileForm.companyName} onChange={(e) => setProfileForm((f) => ({ ...f, companyName: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">Company location</label>
                            <input className="input" value={profileForm.companyLocation} onChange={(e) => setProfileForm((f) => ({ ...f, companyLocation: e.target.value }))} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="input-label">Expertise</label>
                            <input className="input" value={profileForm.expertiseAreas} onChange={(e) => setProfileForm((f) => ({ ...f, expertiseAreas: e.target.value }))} />
                          </div>
                          <div>
                            <label className="input-label">LinkedIn</label>
                            <input className="input" value={profileForm.linkedinUrl} onChange={(e) => setProfileForm((f) => ({ ...f, linkedinUrl: e.target.value }))} />
                          </div>
                          <div className="sm:col-span-2">
                            <label className="input-label">Profile summary</label>
                            <textarea className="input min-h-[100px]" value={profileForm.profileSummary} onChange={(e) => setProfileForm((f) => ({ ...f, profileSummary: e.target.value }))} />
                          </div>
                        </div>
                        <button type="submit" className="btn-primary" disabled={profileSaving}>{profileSaving ? 'Saving…' : 'Save my profile'}</button>
                      </form>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          );
        }

        if (activeMenu === 'academics') {
          return (
            <div className="space-y-6">
              <div className="par-hero">
                <p className="text-sm text-white/80 m-0">Work · Company · Expertise</p>
                <h2 className="text-2xl font-semibold m-0 mt-1">{profile?.companyName || 'Professional profile'}</h2>
                <p className="mt-2 text-white/85 text-sm m-0">
                  {[profile?.designation, profile?.workingDomain, termLabel ? `${termLabel} term` : null]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              </div>
              <div className="admin-card">
                <div className="admin-card-body">
                  <h2 className="admin-section-title">Professional record</h2>
                  <dl className="profile-grid">
                    <Field label="Designation" value={profile?.designation} />
                    <Field label="Experience" value={profile?.yearsOfExperience ? `${profile.yearsOfExperience} years` : null} />
                    <Field label="Qualification" value={profile?.qualification} />
                    <Field label="Highest qualification" value={profile?.highestQualification} />
                    <Field label="Working domain" value={profile?.workingDomain} />
                    <Field label="Expertise" value={profile?.expertiseAreas} />
                  </dl>
                </div>
              </div>
              <div className="admin-card">
                <div className="admin-card-body">
                  <h2 className="admin-section-title">Company & contact</h2>
                  <dl className="profile-grid">
                    <Field label="Company" value={profile?.companyName} />
                    <Field label="Location" value={profile?.companyLocation} />
                    <Field label="Industry" value={profile?.industry} />
                    <Field label="Contact" value={profile?.contactNumber} />
                    <Field label="City" value={profile?.city} />
                    <Field label="Email" value={profile?.email || user?.email} />
                  </dl>
                </div>
              </div>
            </div>
          );
        }

        if (activeMenu === 'history') {
          const history = workspace?.history || [];
          const childCompleted = (workspace?.studentFeedback?.subjects || []).filter((item) => item.status === 'completed');
          return (
            <div className="space-y-6">
              <div className="admin-card">
                <div className="admin-card-body">
                  <h2 className="admin-section-title">Your submitted feedback</h2>
                  <p className="admin-section-subtitle">Read-only. Completed parent feedback cannot be changed.</p>
                  {history.length === 0 ? (
                    <div className="admin-empty">
                      <p className="admin-empty-title">No submissions yet</p>
                      <p className="admin-empty-text">Preview will appear here after you lock a task.</p>
                    </div>
                  ) : (
                    <div className="stu-subject-grid">
                      {history.map((task) => (
                        <TaskCard key={task.id} task={task} onSubmit={handleSubmit} onPreview={handlePreview} />
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="admin-card">
                <div className="admin-card-body">
                  <h2 className="admin-section-title">Student submitted feedback</h2>
                  <p className="admin-section-subtitle">Feedback given by your linked student for the selected academic year.</p>
                  {childCompleted.length === 0 ? (
                    <div className="admin-empty">
                      <p className="admin-empty-title">No student submissions in this view</p>
                      <p className="admin-empty-text">Open My student and pick an academic year to preview completed subject feedback.</p>
                    </div>
                  ) : (
                    <div className="stu-subject-grid">
                      {childCompleted.map((subject) => (
                        <article key={subject.submissionId} className="stu-subject-card is-completed">
                          <div className="flex items-start justify-between gap-3">
                            <div>
                              <p className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>{subject.code}</p>
                              <h3 className="text-lg font-semibold m-0" style={{ color: 'var(--text-primary)' }}>{subject.name}</h3>
                            </div>
                            <span className="stu-chip stu-chip-completed">completed</span>
                          </div>
                          <p className="text-sm m-0" style={{ color: 'var(--text-secondary)' }}>
                            {[subject.facultyName, subject.cycleName, subject.academicYear].filter(Boolean).join(' · ')}
                          </p>
                          <button type="button" className="btn-secondary mt-auto" onClick={() => handleChildPreview(subject)}>Preview</button>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        }

        return null;
      }}
    </RoleDashboardShell>
  );
}
